import { Text } from '@change/ds-core-ui/Text';
import { cn } from '@change/ds-utils';

import { Translate, TranslatePluralHtml } from '@change-corgi/core-react/i18n';

// the petition page's Decision Makers texture, copied: a page's shared/assets can't be imported from another page
import decisionMakerTiles from '#src/app/pages/startAPetitionV2/shared/assets/decision-maker-tiles.jpg';

import { DecisionMakerCarousel } from '../DecisionMakerCarousel';
import { DecisionMakersEmpty } from '../DecisionMakersEmpty';
import { DecisionMakersLoading } from '../DecisionMakersLoading';
import { TrackDecisionMakerSection } from '../TrackDecisionMakerSection';

import { useDecisionMakerContainer } from './hooks/useDecisionMakerContainer';

export const DecisionMakerContainer: React.FC<Props> = ({ className }) => {
	const { data, actions } = useDecisionMakerContainer();

	if (data.status === 'hidden') return null;

	return (
		<div
			ref={data.focusFallbackRef}
			className={cn('bg-gradient-background-weak-3 relative px-8 pt-12 pb-10 md:rounded-lg md:py-10', className)}
			data-qa="decision-makers"
		>
			{/* tiled texture over the gradient, as on the petition page's Decision Makers section */}
			<div
				aria-hidden
				className="pointer-events-none absolute inset-0 bg-repeat opacity-50 md:rounded-lg"
				style={{ backgroundImage: `url(${decisionMakerTiles})` }}
			/>
			<div className="relative z-1 flex flex-col">
				<Text as="h2" id={data.headingId} variant="heading">
					<Translate value="corgi.start_a_petition.decision_makers.title" />
				</Text>
				{/* the intro sits close under the heading; without it, the cards keep the gap they'd have below it */}
				<div className={cn('flex flex-col gap-6', data.showIntro ? 'mt-1' : 'mt-6')}>
					{/* preview has no intro and never loads or empties, so it has nothing to announce */}
					{data.showIntro && (
						<div
							// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- an output can only hold phrasing content, and this holds a paragraph and a card
							role="status"
							className="flex flex-col gap-6"
						>
							<Text as="p" className="text-typography-strong">
								{data.status === 'done' ? (
									// the bold count is markup in the string, so each locale places it
									<TranslatePluralHtml
										value="corgi.start_a_petition.decision_makers.intro"
										count={data.foundCount}
										replacements={{ count: data.foundCount }}
									/>
								) : (
									<Translate value="corgi.start_a_petition.decision_makers.intro_without_count" />
								)}
							</Text>
							{data.status === 'loading' && <DecisionMakersLoading />}
							{data.status === 'empty' && <DecisionMakersEmpty />}
						</div>
					)}
					{data.status === 'done' && (
						<DecisionMakerCarousel
							headingId={data.headingId}
							decisionMakers={data.decisionMakers}
							onRemove={data.removable ? actions.removeDecisionMaker : undefined}
						/>
					)}
				</div>
			</div>
			{/* at the bottom, so the impression means whatever the section shows has scrolled into view; out of flow so
			it takes no space. Keyed by status, so each state remounts it and gets its own view and impression */}
			{data.sectionTracking && (
				<div aria-hidden className="pointer-events-none absolute bottom-0">
					<TrackDecisionMakerSection key={data.status} properties={data.sectionTracking} />
				</div>
			)}
		</div>
	);
};

type Props = {
	className?: string;
};
